import { useEffect, useMemo, useRef, useState } from 'react';
import { fetchTechnicianPanelActiveLunchBatch } from '../../technician-panel/services/technicianPanelApi';

/** Com técnicos em almoço: refresh útil para o ícone. */
export const LUNCH_POLL_ACTIVE_MS = 60_000;
/** Sem ninguém em almoço: cadência lenta (menos carga). */
export const LUNCH_POLL_IDLE_MS = 180_000;
/** Debounce ao mudar colunas/IDs visíveis (evita rajada). */
export const LUNCH_IDS_DEBOUNCE_MS = 800;

export function lunchFingerprint(list) {
  if (!Array.isArray(list) || !list.length) return '';
  return list
    .map((row) => `${String(row?.technicianId ?? '').trim()}\t${String(row?.startedAt ?? '')}`)
    .sort()
    .join('\n');
}

export function setEntriesIfChanged(setEntries, next) {
  const list = Array.isArray(next) ? next : [];
  setEntries((prev) => (lunchFingerprint(prev) === lunchFingerprint(list) ? prev : list));
}

export function normalizeLunchTechnicianIds(technicianIds) {
  if (!Array.isArray(technicianIds)) return [];
  return [...new Set(technicianIds.map((id) => String(id ?? '').trim()).filter(Boolean))];
}

/** Intervalo seguinte: ativo se houver almoço aberto; senão idle. */
export function resolveLunchPollMs(lunches) {
  return Array.isArray(lunches) && lunches.length > 0 ? LUNCH_POLL_ACTIVE_MS : LUNCH_POLL_IDLE_MS;
}

export function buildLunchStartedAtMap(entries) {
  /** @type {Map<string, string>} */
  const map = new Map();
  (Array.isArray(entries) ? entries : []).forEach((row) => {
    const startedAt = row?.startedAt != null ? String(row.startedAt) : '';
    if (!startedAt) return;
    const raw = String(row.technicianId ?? '').trim();
    if (!raw) return;
    map.set(raw, startedAt);
    const digits = raw.replace(/\D/g, '');
    if (digits) {
      map.set(digits, startedAt);
      map.set(digits.padStart(4, '0').slice(-4), startedAt);
    }
  });
  return map;
}

/**
 * Batch de técnicos em almoço aberto (LCMY_GO_LUNCH_BREAK) para ícone no Board.
 * Poll adaptativo + pausa com aba oculta + debounce de IDs.
 *
 * @param {string[]} technicianIds codtec / ids visíveis no board
 * @param {{ enabled?: boolean }} [options] enabled=false para não pollar (ex.: fora do Board)
 * @returns {Map<string, string>} id normalizado/raw → startedAt ISO UTC
 */
export function useBoardActiveLunchTechnicians(technicianIds, options = {}) {
  const enabled = options.enabled !== false;
  const [entries, setEntries] = useState([]);

  const idsKey = useMemo(() => {
    const list = normalizeLunchTechnicianIds(technicianIds);
    return [...list].sort().join('|');
  }, [technicianIds]);

  const idsRef = useRef([]);
  idsRef.current = normalizeLunchTechnicianIds(technicianIds);

  const prevIdsKeyRef = useRef(null);
  const entriesCountRef = useRef(0);
  entriesCountRef.current = Array.isArray(entries) ? entries.length : 0;

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    let cancelled = false;
    let timeoutId = null;
    let startId = null;

    const clearTimers = () => {
      if (timeoutId != null) {
        window.clearTimeout(timeoutId);
        timeoutId = null;
      }
      if (startId != null) {
        window.clearTimeout(startId);
        startId = null;
      }
    };

    const schedule = (ms) => {
      if (cancelled) return;
      if (timeoutId != null) window.clearTimeout(timeoutId);
      timeoutId = window.setTimeout(() => {
        timeoutId = null;
        void tick();
      }, ms);
    };

    const tick = async () => {
      if (cancelled) return;
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        return;
      }

      const ids = idsRef.current;
      if (!ids.length) {
        setEntriesIfChanged(setEntries, []);
        return;
      }

      try {
        const data = await fetchTechnicianPanelActiveLunchBatch(ids);
        if (cancelled) return;
        const lunches = Array.isArray(data?.lunches) ? data.lunches : [];
        setEntriesIfChanged(setEntries, lunches);
        entriesCountRef.current = lunches.length;
        if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
          return;
        }
        schedule(resolveLunchPollMs(lunches));
      } catch {
        if (cancelled) return;
        setEntriesIfChanged(setEntries, []);
        entriesCountRef.current = 0;
        if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
          return;
        }
        schedule(LUNCH_POLL_IDLE_MS);
      }
    };

    const onVisibility = () => {
      if (cancelled) return;
      if (document.visibilityState === 'hidden') {
        clearTimers();
        return;
      }
      clearTimers();
      void tick();
    };

    const isIdsChange =
      prevIdsKeyRef.current != null && prevIdsKeyRef.current !== idsKey;
    prevIdsKeyRef.current = idsKey;

    const ids = idsRef.current;
    if (!ids.length) {
      setEntriesIfChanged(setEntries, []);
      return undefined;
    }

    const startDelay = isIdsChange ? LUNCH_IDS_DEBOUNCE_MS : 0;
    startId = window.setTimeout(() => {
      startId = null;
      void tick();
    }, startDelay);

    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisibility);
    }

    return () => {
      cancelled = true;
      clearTimers();
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisibility);
      }
    };
  }, [idsKey, enabled]);

  return useMemo(() => buildLunchStartedAtMap(entries), [entries]);
}
